/**
 * Add tasks dialog — pick which tasks the Pomodoro list contains.
 *
 * Empty / <2 chars: today's view (`/tasks/today`) narrowed to tasks that are
 * not done and are due today or overdue (by `dueAt`; the endpoint also returns
 * scheduled/completed-today items, which are dropped here). ≥2 chars: open-task
 * search. Tasks already in the list start ticked; saving replaces the list,
 * keeping existing order for kept ids and appending newly ticked ones.
 *
 * "New task" covers the unplanned-urgent case: title only, created as Next,
 * scheduled and due today, then added to the list (with any pending ticks) as
 * the current focus, and the dialog closes, straight into focus mode.
 */

import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { DateChip, Icon, PriorityDot } from '@k-os/ui';
import { useCreateTask, useTasksSearch, useTasksToday, type TaskDto } from '../../api/tasks';
import { usePomodoroStore } from '../../lib/pomodoro-store';
import { endOfToday, toRowModel } from '../../screens/_task-row';
import styles from './AddTasksDialog.module.css';

export interface AddTasksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Returns true when it consumed Esc (backed out of the inline form). */
type EscapeHandler = MutableRefObject<(() => boolean) | null>;

function Body({ onClose, escapeRef }: { onClose: () => void; escapeRef: EscapeHandler }) {
  const taskIds = usePomodoroStore((s) => s.taskIds);
  const setTasks = usePomodoroStore((s) => s.setTasks);
  const setFocus = usePomodoroStore((s) => s.setFocus);
  const create = useCreateTask();
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  // Radix handles Esc at the document level before the input sees it, so the
  // dialog asks us first: Esc backs out of the inline form, then closes.
  useEffect(() => {
    escapeRef.current = () => {
      if (!creating) return false;
      setCreating(false);
      return true;
    };
    return () => {
      escapeRef.current = null;
    };
  }, [creating, escapeRef]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>(() => [...taskIds]);

  const trimmed = q.trim();
  const searching = trimmed.length >= 2;
  const today = useTasksToday();
  const search = useTasksSearch(trimmed, 'open');
  const query = searching ? search : today;

  const tasks: TaskDto[] = useMemo(() => {
    const all = query.data?.tasks ?? [];
    if (searching) return all.filter((t) => t.status !== 'done');
    const end = endOfToday().getTime();
    return all
      .filter((t) => t.status !== 'done' && t.dueAt && Date.parse(t.dueAt) <= end)
      .sort((a, b) => Date.parse(a.dueAt ?? '') - Date.parse(b.dueAt ?? ''));
  }, [query.data, searching]);

  const toggle = (id: string) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const commit = (extra: string[] = []) => {
    const kept = taskIds.filter((id) => selected.includes(id));
    const added = [...selected, ...extra].filter((id) => !taskIds.includes(id));
    setTasks([...kept, ...added]);
  };

  const save = () => {
    commit();
    onClose();
  };

  const startCreating = () => {
    // Searching for something that isn't there is the usual way in: seed the title.
    setNewTitle(trimmed);
    setCreating(true);
  };

  const submitNew = () => {
    const title = newTitle.trim();
    if (!title || create.isPending) return;
    const today = todayAtNoonIso();
    create.mutate(
      { title, status: 'next', scheduledAt: today, dueAt: today },
      {
        onSuccess: ({ task }) => {
          commit([task.id]);
          setFocus(task.id);
          onClose();
        },
      },
    );
  };

  const loading = query.isLoading || (searching && search.isFetching && !search.data);

  return (
    <>
      <div className={styles.searchWrap}>
        <Icon name="search" size={15} className={styles.searchIcon} />
        <input
          autoFocus
          className={styles.search}
          placeholder="Search open tasks…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search tasks"
        />
      </div>

      {creating ? (
        <form
          className={styles.newForm}
          onSubmit={(e) => {
            e.preventDefault();
            submitNew();
          }}
        >
          <input
            autoFocus
            className={styles.newInput}
            placeholder="What needs doing right now?"
            value={newTitle}
            maxLength={500}
            onChange={(e) => setNewTitle(e.target.value)}
            aria-label="New task title"
          />
          <button
            type="submit"
            className={styles.newSubmit}
            disabled={!newTitle.trim() || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create & focus'}
          </button>
          <p className={styles.newHint}>
            {create.isError
              ? "Couldn't create the task. Try again."
              : 'Created as Next, scheduled and due today.'}
          </p>
        </form>
      ) : (
        <button type="button" className={styles.newRow} onClick={startCreating}>
          <Icon name="plus" size={14} />
          {searching ? `Create "${trimmed}" as a new task` : 'New task'}
        </button>
      )}

      <div className={styles.hint}>{searching ? 'Search results' : 'Due today & overdue'}</div>

      <div className={styles.results}>
        {loading ? (
          <p className={styles.state}>Loading…</p>
        ) : query.isError ? (
          <p className={styles.state}>Couldn&apos;t load tasks. Try again.</p>
        ) : tasks.length === 0 ? (
          <p className={styles.state}>
            {searching ? 'No matching open tasks.' : 'Nothing due today or overdue.'}
          </p>
        ) : (
          <ul className={styles.list}>
            {tasks.map((t) => {
              const on = selected.includes(t.id);
              const row = toRowModel(t);
              return (
                <li key={t.id}>
                  <label className={`${styles.item} ${on ? styles.itemOn : ''}`}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={on}
                      onChange={() => toggle(t.id)}
                    />
                    <span className={styles.itemTitle}>{t.title}</span>
                    {row.dateLabel && (
                      <DateChip label={row.dateLabel} state={row.dateState ?? 'normal'} />
                    )}
                    <PriorityDot priority={t.priority} />
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.cancel} onClick={onClose}>
          Cancel
        </button>
        <button type="button" className={styles.save} onClick={save}>
          Save ({selected.length})
        </button>
      </div>
    </>
  );
}

function todayAtNoonIso(): string {
  // Local noon, matching how the date pickers store a calendar day.
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0).toISOString();
}

export function AddTasksDialog({ open, onOpenChange }: AddTasksDialogProps) {
  // Remount the body on each open so selection resets to the current list.
  const [gen, setGen] = useState(0);
  const escapeRef: EscapeHandler = useRef(null);
  useEffect(() => {
    if (open) setGen((g) => g + 1);
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          aria-describedby={undefined}
          onEscapeKeyDown={(e) => {
            if (escapeRef.current?.()) e.preventDefault();
          }}
        >
          <Dialog.Title className={styles.title}>Add tasks</Dialog.Title>
          <Body key={gen} onClose={() => onOpenChange(false)} escapeRef={escapeRef} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
